import { useAuth0 } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import {
	ArrowRightStartOnRectangleIcon,
	Bars3Icon,
	GlobeAltIcon,
	ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDemoSettings } from "../contexts/demo-settings-context";
import { useUserData } from "../contexts/user-data-context";
import { useNotifications } from "../hooks/useNotifications";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import { useAnalytics } from "../lib/analytics";
import { performLogout } from "../lib/auth-logout";
import { useClientTranslations } from "../lib/i18n/client";
import { getBetaTagClassName, getEnvironment } from "../lib/utils/environment";
import { BetaFeedbackModal } from "./BetaFeedbackModal";
import { FeedbackModal } from "./FeedbackModal";
import styles from "./Header.module.css";
import { LanguageSelector } from "./i18n/LanguageSelector";
import { useGlobalLoading } from "./loading/global-loading-context";
import { NotificationDropdown } from "./notifications/NotificationDropdown";
import { PullToRefreshIndicator } from "./PullToRefreshIndicator";
import { SideMenu } from "./SideMenu";
import { UpperNav } from "./UpperNav/UpperNav";

export function Header() {
	const { t } = useClientTranslations("common");
	const navigate = useNavigate();
	const { setLoading } = useGlobalLoading();
	const analytics = useAnalytics();
	const { user, logout } = useAuth0();
	const { userData } = useUserData();
	const queryClient = useQueryClient();
	const { logoUrl: demoLogoUrl } = useDemoSettings();

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
	const [isBetaFeedbackOpen, setIsBetaFeedbackOpen] = useState(false);
	const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
	const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		right: 0,
	});

	const dropdownRef = useRef<HTMLDivElement>(null);
	const profileButtonRef = useRef<HTMLButtonElement>(null);

	const environment = useMemo(() => getEnvironment(), []);
	const betaBadgeClassName = useMemo(
		() => getBetaTagClassName(environment),
		[environment],
	);

	const { notifications, unreadCount, markAsRead, markAllAsRead } =
		useNotifications();

	// Pull-to-refresh functionality - graceful refresh without page reload
	const handleRefresh = useCallback(async () => {
		// Invalidate all queries to refetch fresh data
		await queryClient.invalidateQueries();
	}, [queryClient]);

	const { pullDistance, isRefreshing, isThresholdReached } = usePullToRefresh({
		onRefresh: handleRefresh,
		threshold: 80,
		maxPull: 150,
	});

	// Check role from Auth0 custom claims
	const userRole = user?.["https://eraborn.com/role"] || "member";
	const isAdmin = Boolean(userRole === "admin");

	// Use either user data or Auth0 user data
	const displayName = userData?.displayName || user?.name || "User";
	const displayEmail = user?.email || "";

	// Helper function to check if image is valid
	const isValidImage = (
		imageUrl: string | null | undefined,
	): imageUrl is string => {
		if (
			imageUrl == null ||
			imageUrl.trim() === "" ||
			imageUrl === "null" ||
			imageUrl === "undefined"
		)
			return false;
		if (
			imageUrl.includes("gravatar.com") ||
			imageUrl.includes("auth0.com/avatars")
		)
			return false;
		return true;
	};

	const currentUserImage = userData?.userImage || user?.picture;
	const validUserImage = isValidImage(currentUserImage)
		? currentUserImage
		: null;

	// Close dropdown when clicking outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsDropdownOpen(false);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleProfileDropdown = () => {
		if (profileButtonRef.current) {
			const rect = profileButtonRef.current.getBoundingClientRect();
			const isRTL = document.documentElement.dir === "rtl";
			setDropdownPosition({
				top: rect.bottom + 8,
				right: isRTL
					? window.innerWidth - rect.left - 192
					: window.innerWidth - rect.right,
			});
		}
		setIsDropdownOpen(!isDropdownOpen);
		analytics.logEvent("profile_dropdown_click");
	};

	const handleSignOut = async () => {
		analytics.logAuth("logout", {
			trigger: "header_menu",
			platform: Capacitor.getPlatform(),
		});
		await performLogout(logout);
	};

	// Turn off loading when this component loads
	useEffect(() => {
		setLoading(false);
	}, [setLoading]);

	return (
		<>
			{/* Pull-to-Refresh Indicator */}
			<PullToRefreshIndicator
				pullDistance={pullDistance}
				isRefreshing={isRefreshing}
				isThresholdReached={isThresholdReached}
			/>

			<div className={styles.fixedTop}>
				<header className={styles.header}>
					{/* Left zone - Hamburger menu */}
					<div className={styles.leftZone}>
						<button
							type="button"
							className={styles.hamburgerButton}
							onClick={() => {
								setIsSideMenuOpen(true);
								analytics.logEvent("hamburger_menu_click");
							}}
							aria-label={t("navigation.menu")}
						>
							<Bars3Icon className={styles.hamburgerIcon} />
						</button>
					</div>

					{/* Center zone - Logo + Beta */}
					<div className={styles.centerZone}>
						<Link
							to="/"
							className={styles.logoLink}
							onClick={() => analytics.logEvent("logo_click")}
						>
							<img
								src={demoLogoUrl || "/images/era-text-black.png"}
								alt="Era"
								className={styles.logoImage}
							/>
						</Link>
						<button
							type="button"
							onClick={() => {
								setIsBetaFeedbackOpen(true);
								analytics.logEvent("beta_tag_click");
							}}
							className={styles[betaBadgeClassName]}
						>
							<div className={styles.betaBadgeText}>Beta</div>
						</button>
					</div>

					{/* Right zone - Profile */}
					<div className={styles.rightZone}>
						<div className={styles.profileContainer} ref={dropdownRef}>
							<button
								ref={profileButtonRef}
								type="button"
								onClick={handleProfileDropdown}
								className={styles.profileButton}
								aria-label={t("navigation.profileMenu")}
							>
								{validUserImage ? (
									<img
										src={validUserImage}
										alt=""
										className={styles.profileImage}
									/>
								) : (
									<span className={styles.profileInitial}>
										{displayName.charAt(0).toUpperCase()}
									</span>
								)}
								{unreadCount > 0 && (
									<span className={styles.profileNotificationDot} />
								)}
							</button>

							{isDropdownOpen && (
								<div
									className={styles.dropdown}
									style={{
										top: `${dropdownPosition.top}px`,
										right: `${dropdownPosition.right}px`,
									}}
								>
									{/* User info section */}
									<div className={styles.dropdownUserInfo}>
										<div className={styles.dropdownUserDetails}>
											<div className={styles.dropdownUserName}>
												<p className={styles.dropdownUserNameText}>
													{displayName}
												</p>
												{isAdmin && (
													<span className={styles.dropdownAdminBadge}>
														{t("navigation.admin")}
													</span>
												)}
											</div>
											<p className={styles.dropdownUserEmail}>{displayEmail}</p>
										</div>
									</div>

									{/* Notifications Section */}
									<div className={styles.dropdownNotificationsSection}>
										<div className={styles.dropdownNotificationsHeader}>
											<div className={styles.dropdownNotificationsTitle}>
												{t("notifications.title")}
												{unreadCount > 0 && (
													<span className={styles.dropdownNotificationBadge}>
														{unreadCount > 99 ? "99+" : unreadCount}
													</span>
												)}
											</div>
										</div>
										<div className={styles.dropdownNotificationsList}>
											{notifications.length > 0 ? (
												<>
													{notifications.slice(0, 3).map((notification) => (
														<button
															key={notification.id}
															type="button"
															className={`${styles.dropdownNotificationItem} ${
																!notification.isRead ? styles.unread : ""
															}`}
															onClick={() => {
																analytics.logEvent(
																	"notification_clicked",
																	undefined,
																	{
																		notification_id: notification.id,
																		has_action_url: String(
																			!!notification.actionUrl,
																		),
																		is_read: String(notification.isRead),
																	},
																);
																markAsRead(notification.id);
																if (notification.actionUrl) {
																	navigate(notification.actionUrl);
																	setIsDropdownOpen(false);
																}
															}}
														>
															<div
																className={styles.dropdownNotificationContent}
															>
																<p
																	className={styles.dropdownNotificationMessage}
																>
																	{notification.content}
																</p>
																<p className={styles.dropdownNotificationTime}>
																	{formatDistanceToNow(
																		new Date(notification.createdAt),
																		{
																			addSuffix: true,
																		},
																	)}
																</p>
															</div>
														</button>
													))}
													<button
														type="button"
														className={styles.dropdownViewAllButton}
														onClick={() => {
															setIsDropdownOpen(false);
															setIsNotificationsOpen(true);
															analytics.logEvent(
																"notifications_view_all_click",
															);
														}}
													>
														{t("notifications.viewAll") ||
															"View all notifications"}
													</button>
												</>
											) : (
												<p className={styles.dropdownNoNotifications}>
													{t("notifications.noNotifications") ||
														"No notifications"}
												</p>
											)}
										</div>
									</div>

									{isAdmin && (
										<Link
											to="/admin"
											className={styles.dropdownLink}
											onClick={() => {
												setIsDropdownOpen(false);
												analytics.logEvent("admin_click");
											}}
										>
											<div className={styles.dropdownItemContent}>
												<ShieldCheckIcon className={styles.dropdownIcon} />
												<span>{t("navigation.admin")}</span>
											</div>
										</Link>
									)}

									{/* Language Selector */}
									<div className={styles.dropdownLanguageSection}>
										<div className={styles.dropdownLanguageLabel}>
											<GlobeAltIcon className={styles.dropdownIcon} />
											{t("general.displayLanguage")}
										</div>
										<LanguageSelector variant="compact" className="w-full" />
									</div>

									<button
										type="button"
										onClick={handleSignOut}
										className={styles.dropdownButton}
									>
										<div className={styles.dropdownItemContent}>
											<ArrowRightStartOnRectangleIcon
												className={styles.dropdownIcon}
											/>
											<span>{t("buttons.signOut")}</span>
										</div>
									</button>
								</div>
							)}
						</div>
					</div>
				</header>
				<UpperNav />
			</div>

			{/* Side Menu */}
			<SideMenu
				isOpen={isSideMenuOpen}
				onClose={() => setIsSideMenuOpen(false)}
			/>

			{/* Feedback Modal */}
			<FeedbackModal
				isOpen={isFeedbackOpen}
				onClose={() => setIsFeedbackOpen(false)}
				messageId={null}
				chatId={null}
			/>

			{/* Beta Feedback Modal */}
			<BetaFeedbackModal
				isOpen={isBetaFeedbackOpen}
				onClose={() => setIsBetaFeedbackOpen(false)}
			/>

			{/* Notification Dropdown */}
			<NotificationDropdown
				isOpen={isNotificationsOpen}
				onClose={() => setIsNotificationsOpen(false)}
				notifications={notifications}
				onMarkAsRead={markAsRead}
				onMarkAllAsRead={markAllAsRead}
			/>
		</>
	);
}
